import React, { useState, useEffect, useRef } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import "../styles/chessGame.css";

const ChessGame = ({ fen, solution, puzzleId,explaination, onSolved, onNextPuzzle }) => {
  const [game, setGame] = useState(() => new Chess());
  const [moveLog, setMoveLog] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [isSolved, setIsSolved] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isHintPreview, setIsHintPreview] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [invalidMoveSquares, setInvalidMoveSquares] = useState({});
  const [showInstruction, setShowInstruction] = useState(false);

  const rewardGivenRef = useRef(false);

  /* =========================
     INIT / RESET PUZZLE
  ========================= */
  useEffect(() => {
    if (!fen) return;

    const g = new Chess();
    g.load(fen);

    setGame(g);
    setMoveLog([]);
    setStepIndex(0);
    setIsSolved(false);
    setFeedback(null);
    rewardGivenRef.current = false;
  }, [fen, puzzleId]);

  /* =========================
     HANDLE PLAYER MOVE
  ========================= */
function onPieceDrop(from, to) {
  if (isSolved) return false;

  let gameCopy;
  let move;

  try {
    gameCopy = new Chess(game.fen());
    move = gameCopy.move({
      from,
      to,
      promotion: "q",
    });
  } catch (err) {
    // ❌ ILLEGAL MOVE (chess.js throws)
    setInvalidMoveSquares({
      [from]: { backgroundColor: "rgba(255, 0, 0, 0.45)" },
      [to]: { backgroundColor: "rgba(255, 0, 0, 0.45)" },
    });

    setTimeout(() => setInvalidMoveSquares({}), 400);
    return false;
  }

  // 🛡 Extra safety (in case chess.js changes behavior again)
  if (!move) {
    return false;
  }

  // ✅ LEGAL MOVE — CHECK PUZZLE VALIDITY
  const playedMove = move.san;
  const expectedMove = solution[stepIndex];

  if (playedMove !== expectedMove) {
    setFeedback("wrong");
    resetPuzzle();
    autoHideFeedback();
    return false;
  }

  // ✅ CORRECT MOVE
  setGame(gameCopy);
  setMoveLog((prev) => [...prev, move.san]);

  const nextStep = stepIndex + 1;
  setStepIndex(nextStep);

  if (nextStep >= solution.length) {
    finishPuzzle();
    return true;
  }

  setTimeout(() => {
    playOpponentMove(gameCopy, nextStep);
  }, 600);

  return true;
}

  /* =========================
   CLICK MOVE SUPPORT
========================= */
  function onSquareClick(square) {
    if (isSolved || isHintPreview) return;

    // 1️⃣ No piece selected yet → select if piece exists
    if (!selectedSquare) {
      const piece = game.get(square);
      if (piece) {
        setSelectedSquare(square);
      }
      return;
    }

    // 2️⃣ Same square clicked again → deselect
    if (selectedSquare === square) {
      setSelectedSquare(null);
      return;
    }

    // 3️⃣ Try move using existing logic
    const success = onPieceDrop(selectedSquare, square);

    // Clear selection whether move succeeded or failed
    setSelectedSquare(null);

    return success;
  }

  /* =========================
     AUTO OPPONENT MOVE
  ========================= */
  function playOpponentMove(currentGame, index) {
    const opponentMove = solution[index];
    if (!opponentMove) return;

    const gameCopy = new Chess(currentGame.fen());
    const move = gameCopy.move(opponentMove);
    if (!move) return;

    setGame(gameCopy);
    setMoveLog((prev) => [...prev, move.san]);

    const nextStep = index + 1;
    setStepIndex(nextStep);

    if (nextStep >= solution.length) {
      finishPuzzle();
    }
  }

  /* =========================
     FINISH PUZZLE
  ========================= */
  function finishPuzzle() {
    setIsSolved(true);
    setFeedback("correct");

    if (!rewardGivenRef.current && onSolved) {
      rewardGivenRef.current = true;
      onSolved(puzzleId);
    }
  }

  
/* =========================
   HINT (PLAY NEXT SOLUTION MOVE)
========================= */
const handleHint = () => {
  if (isSolved || isHintPreview) return;

  const nextMoveSan = solution[stepIndex];
  if (!nextMoveSan) return;

  const gameCopy = new Chess(game.fen());
  const move = gameCopy.move(nextMoveSan);
  if (!move) return;

  // 👀 Show move temporarily
  setIsHintPreview(true);
  setGame(gameCopy);
  setMoveLog((prev) => [...prev, `Hint: ${move.san}`]);

  // ⏳ Reset after 1.2 seconds
  setTimeout(() => {
    const resetGame = new Chess();
    resetGame.load(fen);

    setGame(resetGame);
    setMoveLog([]);
    setStepIndex(0);
    setIsHintPreview(false);
  }, 1200);
};


  /* =========================
     NEXT PUZZLE (SHARED)
  ========================= */
  const goToNextPuzzle = () => {
    setFeedback(null);
    onNextPuzzle?.();
  };

  /* =========================
     RESET
  ========================= */
  function resetPuzzle() {
    const g = new Chess();
    g.load(fen);
    setGame(g);
    setMoveLog([]);
    setStepIndex(0);
    setIsSolved(false);
  }

  function autoHideFeedback() {
    setTimeout(() => setFeedback(null), 1500);
  }
const customSquareStyles = {
  ...(selectedSquare
    ? {
        [selectedSquare]: {
          backgroundColor: "rgba(255, 215, 0, 0.6)",
        },
      }
    : {}),
  ...invalidMoveSquares,
};

  return (
    <div className="chess-game">
      <div className="board-section board-feedback-wrapper">
        {feedback && (
  <div className={`board-feedback-box ${feedback}`}>

    {/* 🪙 GOLD COIN ANIMATION */}
    {feedback === "correct" && (
      <div className="gold-coin-animation">🪙</div>
    )}

    <div className="feedback-icon">
      {feedback === "correct" ? "✓" : "✕"}
    </div>

    <div className="feedback-text">
      {feedback === "correct"
        ? "Puzzle Solved (+5 Gold)"
        : "Wanna try again?"}
    </div>

    {feedback === "correct" && (
      <button className="next-puzzle-btn" onClick={goToNextPuzzle}>
        Next Puzzle →
      </button>
    )}
  </div>
)}


        <div className="board-wrapper-course">
          <Chessboard
            key={`${fen}-${puzzleId}`}
            position={game.fen()}
            onPieceDrop={onPieceDrop}
            onSquareClick={onSquareClick}          // ✅ click support
            customSquareStyles={customSquareStyles} // ✅ highlight
            arePiecesDraggable={!isSolved && !isHintPreview}
          />
        </div>
      </div>

      {/* MOVE HISTORY */}
              <div className="move-history">
                <div className="move-history-header">
          {explaination && (
            <button
              className="instruction-icon-btn"
              onClick={() => setShowInstruction(true)}
              title="View Instruction"
            >
              ℹ️
            </button>
          )}
          <span className="move-history-title">Moves</span>
        </div>
        <div className="move-history-body">
          <div className="move-table">
            {moveLog.length === 0 && (
              <div className="no-moves">No moves yet</div>
            )}

            {Array.from({ length: Math.ceil(moveLog.length / 2) }).map(
              (_, i) => (
                <div key={i} className="move-row">
                  <div className="move-no">{i + 1}.</div>
                  <div className="move white">{moveLog[i * 2] || "-"}</div>
                  <div className="move black">
                    {moveLog[i * 2 + 1] || "-"}
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* 🚀 ALWAYS WORKS (SKIP) */}
        <div className="move-history-footer">
          <button className="hint-btn" disabled={isHintPreview} onClick={handleHint}>
          Hint ♟️
          </button>


          <button className="next-puzzle-btn" onClick={goToNextPuzzle}>
            Skip / Next Puzzle →
          </button>
        </div>
    </div>
    {/* 🧠 INSTRUCTION POPUP */}
{showInstruction && (
  <div
    className="instruction-overlay"
    onClick={() => setShowInstruction(false)}
  >
    <div
      className="instruction-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="instruction-modal-header">
        <span>Instruction</span>
        <button
          className="instruction-close"
          onClick={() => setShowInstruction(false)}
        >
          ✕
        </button>
      </div>

      <div className="instruction-modal-body">
        {explaination}
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default ChessGame;
